import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { colors, Card, CardContent, CardHeader, Checkbox, Grid, Paper, IconButton, InputBase } from '@mui/material';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';

import ModalDialog from '../../../../components/ModalDialog/ModalDialog.js';
import UploadSelectedFilesTable from './UploadSelectedFilesTable';

import { SetSelectedFiles, GetKpiFilesTree, RunKpiFilesParser, SearchFTPDirectory } from '../../actions/DriveTestUploadActions';

const useStyles = makeStyles((theme) => ({
	root: {
		width: 960,
	},
	header: {
		padding: theme.spacing(3),
		maxWidth: 720,
		margin: '0 auto',
	},
	content: {
		padding: theme.spacing(0, 2),
		maxWidth: 720,
		margin: '0 auto',
	},
	helperText: {
		textAlign: 'right',
		marginRight: 0,
	},
	author: {
		margin: theme.spacing(4, 0),
		display: 'flex',
	},
	avatar: {
		marginRight: theme.spacing(2),
	},
	actions: {
		backgroundColor: colors.grey[100],
		padding: theme.spacing(2),
		display: 'flex',
		alignContent: 'right',
	},
	applyButton: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const KpisUploadDialog = (props) => {
	const { author, open, onClose, onApply, className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const userId = useSelector(state => state.session.user.id);
	const [searchPath, setSearchPath] = useState('');
	const kpiFilesTree = useSelector((state) => state.driveTestUploadReducer.kpiFilesTree);
	const setSelectedFiles = selectedFiles => dispatch(SetSelectedFiles(selectedFiles));
	const selectedFiles = useSelector((state) => state.driveTestUploadReducer.selectedFiles);
	const [selectedFilesHelper, setSelectedFilesHelper] = useState([]);

	useEffect(() => {
		dispatch(GetKpiFilesTree(userId));
	}, []);

	const _onConfirmClick = () => {
		dispatch(RunKpiFilesParser(userId, selectedFiles));
	};

	const _onSearchClick = () => {
		dispatch(SearchFTPDirectory(userId, searchPath));
	};

	const _handleSearchChange = (event) => {
		setSearchPath(event.target.value);
	};

	// Recursive helper function to get all child IDs
	const getAllChildIds = (children) => {
		if (!children || !children.length) return [];
		return children.reduce((acc, child) => [...acc, child.id, ...getAllChildIds(child.children)], []);
	};

	const getFileType = (filePath) => {
		if (filePath.includes('FTP') && filePath.includes('UL')) {
			return 'Packet UL';
		} if (filePath.includes('FTP') && filePath.includes('DL')) {
			return 'Packet DL';
		} if (filePath.includes('SCANNER')) {
			return 'Scanner';
		} if (filePath.includes('VOLTE') && filePath.includes('MO')) {
			return 'Voice MO';
		} if (filePath.includes('VOLTE') && filePath.includes('MT')) {
			return 'Voice MT';
		}
		return 'Unknown'; // Default case if no condition is met
	};

	// Handle checkbox toggle (only files should be selected, not folders)
	const handleToggle = (event, node, path = []) => {
		event.stopPropagation(); // Prevents expanding/collapsing when clicking checkbox

		const fullPath = [...path, node.name].join('/'); // Full path to the node

		if (!node.isDirectory) { // Only toggle files, not directories
			// Update selectedFilesHelper
			setSelectedFilesHelper((prevSelected) => {
				const newSelectedFiles = prevSelected.includes(fullPath)
					? prevSelected.filter((file) => file !== fullPath) // Deselect file
					: [...prevSelected, fullPath]; // Select file

				// Update selectedFilesObject based on newSelectedFiles
				const updatedFilesObject = newSelectedFiles.map(file => {
					// Check if the file already exists in selectedFilesObject
					const existingFileObject = selectedFiles.find(obj => obj.file === file);
					const newFileType = getFileType(file);
					return {
						fileDirectory: existingFileObject ? existingFileObject.fileDirectory : kpiFilesTree.directory,
						file,
						fileType: existingFileObject ? existingFileObject.fileType : newFileType // Preserve fileType
					};
				});

				// Update the selectedFilesObject state
				setSelectedFiles(updatedFilesObject);
				return newSelectedFiles; // Return the updated selected files
			});
		}
	};

	// Recursively render tree nodes with checkboxes
	const renderTree = (node, path = []) => (
		<TreeItem
			key={node.name}
			nodeId={[...path, node.name].join('/')}
			label={(
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{!node.isDirectory && ( // Render checkbox only for files
						<Checkbox
							checked={selectedFilesHelper.includes([...path, node.name].join('/'))}
							onClick={(event) => {
								event.stopPropagation(); // This prevents the tree from collapsing when clicking checkbox
								handleToggle(event, node, path); // Pass node and current path
							}}
						/>
					)}
					{node.name}
				</div>
			)}
		>
			{Array.isArray(node.children) && node.children.length > 0
				? node.children.map((childNode) => renderTree(childNode, [...path, node.name]))
				: null}
		</TreeItem>
	);

	return (
		<ModalDialog
			maxWidth={false}
			onClose={onClose}
			open={open}
			title="Kpis Upload"
			confirmLabel="Run"
			hasConfirm
			onConfirmClicked={_onConfirmClick}
		>
			<div className={classes.fields}>
				<Grid container spacing={1}>
					<Grid item md={4} xs={4}>
						<Card className={classes.container} container spacing={3} style={{ minHeight: '75vh', maxHeight: '75vh', overflowY: 'auto' }}>
							<Paper
								component="form"
								sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}
							>
								<InputBase
									sx={{ ml: 1, flex: 1 }}
									placeholder="Search FTP Directory"
									inputProps={{ 'aria-label': 'search ftp dir' }}
									value={searchPath}
									onChange={_handleSearchChange}
								/>
								<IconButton type="button" sx={{ p: '10px' }} aria-label="search" onClick={_onSearchClick}>
									<SearchIcon />
								</IconButton>
							</Paper>
							<CardHeader title={kpiFilesTree.directory} />
							<CardContent>
								<TreeView
									defaultCollapseIcon={<ExpandMoreIcon />}
									defaultExpandIcon={<ChevronRightIcon />}
									sx={{ overflowX: 'hidden', minHeight: 270, flexGrow: 1 }}
								>
									{kpiFilesTree.ftpFileInfo.map((treeItem) => renderTree(treeItem))}
								</TreeView>
							</CardContent>
						</Card>
					</Grid>
					<Grid item md={8} xs={8}>
						<Card className={classes.container} container spacing={3} style={{ minHeight: '75vh', maxHeight: '75vh', overflowY: 'auto' }}>
							<CardContent>
								<UploadSelectedFilesTable className={classes.results} data={selectedFiles} />
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</div>
		</ModalDialog>
	);
};

KpisUploadDialog.propTypes = {
	author: PropTypes.object.isRequired,
	className: PropTypes.string,
	onApply: PropTypes.func,
	onClose: PropTypes.func,
	open: PropTypes.bool.isRequired
};

export default KpisUploadDialog;
